import React, { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer } from 'react';

import axios, { AxiosResponse } from 'axios';

import { FeatureFlagId, isFeatureFlagEnabled } from 'admin/feature-flags/FeatureFlags';
import {
    FilterDefinition,
    FilterValues,
    GetFilterDefinitionsArgs,
    GetFilterDefinitionsRequest,
    GetFilterValuesRequest,
    WidgetType,
} from 'common/filters/Filters.types';
import { Filter as SearchFilter, Filter } from 'core/util/Search';
import { SurveyTemplateFilter } from 'surveys/common/types/Filter';
import {
    SURVEY1_TEMPLATE_ID,
    SURVEY1_TEMPLATE_NAME,
    KIOSK_TEMPLATE_ID,
    KIOSK_TEMPLATE_NAME,
} from 'surveys/common/types/R4ESurvey3Common';

export interface FiltersContainer {
    selectedFilters?: SearchFilter[];
    selectedChartFilters?: SearchFilter[];
    loading: boolean;
    error?: object;
}

export enum FiltersActionType {
    ChangeStart,
    ChangeReset,
    ChangeSuccess,
    ChangeError,
    ChangeRequest,
    SetChartFilters,
    SetFilters,
}

export interface FiltersAction {
    type: FiltersActionType;
    selectedFilters?: SearchFilter[];
    selectedChartFilters?: SearchFilter[];
    error?: object;
}

interface FiltersProviderProps {
    children: ReactNode;
}

let filterDefinitions: FilterDefinition[];
let searchFilters: SearchFilter[];

export const FiltersStateContext = createContext<FiltersContainer | undefined>(undefined);
export const FiltersDispatchContext = createContext<Dispatch<FiltersAction> | undefined>(undefined);

const filtersReducer = (filtersContainer: FiltersContainer, action: FiltersAction): FiltersContainer => {
    switch (action.type) {
        case FiltersActionType.ChangeStart:
            return { loading: true };

        case FiltersActionType.ChangeReset:
            return { loading: true, selectedFilters: action.selectedFilters };
        case FiltersActionType.ChangeSuccess:
            return { loading: false, selectedFilters: action.selectedFilters };

        case FiltersActionType.ChangeError:
            return { loading: false, error: action.error };

        case FiltersActionType.ChangeRequest:
            return { loading: true, selectedFilters: action.selectedFilters };

        case FiltersActionType.SetChartFilters:
            return {
                ...filtersContainer,
                selectedChartFilters: action.selectedChartFilters,
            };
        default:
            throw new TypeError(`unrecognized action type ${action.type}`);
    }
};

export const FiltersProvider = ({ children }: FiltersProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(filtersReducer, { loading: true });

    return (
        <FiltersStateContext.Provider value={state}>
            <FiltersDispatchContext.Provider value={dispatch}>{children}</FiltersDispatchContext.Provider>
        </FiltersStateContext.Provider>
    );
};

const useFiltersState = (): FiltersContainer => {
    const context = useContext(FiltersStateContext);

    if (context === undefined) {
        throw new Error('useFiltersState must be used within a FiltersProvider');
    }

    return context;
};

const useFiltersDispatch = (): Dispatch<FiltersAction> => {
    const context = useContext(FiltersDispatchContext);

    if (context === undefined) {
        throw new Error('useFiltersDispatch must be used within a FiltersProvider');
    }

    return context;
};

export const useFilters = (): [FiltersContainer, Dispatch<FiltersAction>] => {
    const [filtersContainer, dispatch] = [useFiltersState(), useFiltersDispatch()];

    useEffect(() => {
        if (!filtersContainer.loading && !filtersContainer.selectedFilters && filterDefinitions) {
            try {
                searchFilters = filterDefinitions
                    .filter((filterDefinition) => filterDefinition.selected)
                    .map((filterDefinition) => {
                        return {
                            name: filterDefinition.name,
                            values: filterDefinition.values
                                .filter((filterValue) => filterValue.selected)
                                .map((filterValue) => {
                                    return filterValue.value;
                                }),
                        } as SearchFilter;
                    });

                dispatch({ type: FiltersActionType.ChangeSuccess, selectedFilters: searchFilters });
            } catch (error) {
                dispatch({ type: FiltersActionType.ChangeError });
            }
        }
    }, [filtersContainer.selectedFilters, filtersContainer.loading, dispatch]);

    return [filtersContainer, dispatch];
};

export const isMultiSelect = (filterDefinition: FilterDefinition): boolean => {
    return filterDefinition.widgetType === WidgetType.MultiSelect;
};

export const getFilterDefinitions = async (
    agencyId: number,
    tenantId: number,
    request: GetFilterDefinitionsRequest,
    args: GetFilterDefinitionsArgs,
    post?: boolean,
): Promise<Array<FilterDefinition>> => {
    let modifiedRequest = request;

    if (request.selectedFilters && request.selectedFilters.length > 0) {
        const selectedTemplates = request.selectedFilters.find(
            (filter: SearchFilter) => filter.name === SurveyTemplateFilter.TemplateId,
        );

        if (selectedTemplates?.values.length === 1 && selectedTemplates.values.includes(SURVEY1_TEMPLATE_ID)) {
            modifiedRequest = {
                ...request,
                selectedFilters: request.selectedFilters.filter(
                    (filter) => !(filter.name === SurveyTemplateFilter.TemplateId),
                ),
            };
        } else if (selectedTemplates?.values.length === 1 && selectedTemplates.values.includes(KIOSK_TEMPLATE_ID)) {
            modifiedRequest = {
                ...request,
                selectedFilters: request.selectedFilters.filter(
                    (filter) => !(filter.name === SurveyTemplateFilter.TemplateId),
                ),
            };
        }
    }
    const modifiedParams = { agencyId, tenantId };
    let response: AxiosResponse;

    if (post) {
        response = await axios.post(
            `/api/tenants/${tenantId}/filters`,
            {
                request: modifiedRequest,
                args,
            },
            {
                params: modifiedParams,
            },
        );
    } else {
        response = await axios.get(`/api/tenants/${tenantId}/filters`, {
            params: { ...modifiedParams, request: modifiedRequest, args },
        });
    }

    const surveyTemplateFilterIndex = response.data.findIndex(
        (filter: FilterDefinition) => filter.name === SurveyTemplateFilter.TemplateId,
    );

    if (surveyTemplateFilterIndex !== -1) {
        if (request.selectedFilters && request.selectedFilters.length > 0) {
            const selectedTemplates = request.selectedFilters.find(
                (filter: SearchFilter) => filter.name === SurveyTemplateFilter.TemplateId,
            );

            if (
                selectedTemplates &&
                selectedTemplates.values &&
                selectedTemplates.values.includes(SURVEY1_TEMPLATE_ID)
            ) {
                response.data[surveyTemplateFilterIndex].values.push({
                    label: SURVEY1_TEMPLATE_NAME,
                    value: SURVEY1_TEMPLATE_ID,
                    selected: true,
                });
                response.data[surveyTemplateFilterIndex].selected = true;
            } else {
                response.data[surveyTemplateFilterIndex].values.push({
                    label: SURVEY1_TEMPLATE_NAME,
                    value: SURVEY1_TEMPLATE_ID,
                    selected: false,
                });
            }

            // Add Kiosk template option only if migration is enabled for this tenant
            try {
                const isKioskMigrationEnabled = await isFeatureFlagEnabled(
                    tenantId,
                    FeatureFlagId.KioskSurveyMigration,
                );

                if (isKioskMigrationEnabled.enabled) {
                    if (
                        selectedTemplates &&
                        selectedTemplates.values &&
                        selectedTemplates.values.includes(KIOSK_TEMPLATE_ID)
                    ) {
                        response.data[surveyTemplateFilterIndex].values.push({
                            label: KIOSK_TEMPLATE_NAME,
                            value: KIOSK_TEMPLATE_ID,
                            selected: true,
                        });
                        response.data[surveyTemplateFilterIndex].selected = true;
                    } else {
                        response.data[surveyTemplateFilterIndex].values.push({
                            label: KIOSK_TEMPLATE_NAME,
                            value: KIOSK_TEMPLATE_ID,
                            selected: false,
                        });
                    }
                }
            } catch (error) {
                // Log the error so feature flag issues can be diagnosed
                // without breaking filter loading entirely.
                // eslint-disable-next-line no-console
                console.error('Failed to resolve KioskSurveyMigration feature flag', error);
            }
        }
    }
    if (args.includeValues) {
        // Store to access locally
        filterDefinitions = response.data;
    }

    return response.data;
};

const { CancelToken } = axios;
let cancelGetFilterValuesSource = CancelToken.source();

export const getFilterValues = async (
    agencyId: number,
    tenantId: number,
    filterName: string,
    request: GetFilterValuesRequest,
    args?: any,
): Promise<FilterValues> => {
    const params: any = { agencyId, tenantId, filterName };

    // Always use POST
    const response: AxiosResponse = await axios.post(
        `/api/tenants/${tenantId}/filters/${filterName}/values`,
        {
            request,
            args: args || {},
        },
        {
            params,
            cancelToken: cancelGetFilterValuesSource.token,
        },
    );

    if (filterName === SurveyTemplateFilter.TemplateId) {
        if (response.data && response.data.values) {
            response.data.values.push({
                label: SURVEY1_TEMPLATE_NAME,
                value: SURVEY1_TEMPLATE_ID,
            });

            // Add Kiosk template option only if migration is enabled for this tenant
            try {
                const isKioskMigrationEnabled = await isFeatureFlagEnabled(
                    tenantId,
                    FeatureFlagId.KioskSurveyMigration,
                );

                if (isKioskMigrationEnabled.enabled) {
                    response.data.values.push({
                        label: KIOSK_TEMPLATE_NAME,
                        value: KIOSK_TEMPLATE_ID,
                    });
                }
            } catch (error) {
                // Log the error so failures to fetch the feature flag
                // don't silently hide kiosk survey options.
                // eslint-disable-next-line no-console
                console.error('Failed to resolve KioskSurveyMigration feature flag in getFilterValues', error);
            }
        }
    }
    return response.data;
};

export const cancelGetFilterValues = (): void => {
    cancelGetFilterValuesSource.cancel();
    cancelGetFilterValuesSource = CancelToken.source();
};

export const getFilterByName = (filterName: string, filters: SearchFilter[]): Filter | undefined => {
    return filters.find((f) => f.name === filterName);
};

export const getAdvancedFilter = async (tenantId: number, filterId: string): Promise<any> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/advancedFilter/${filterId}`);

    return data;
};

export const saveAdvancedFilter = async (tenantId: number, advanceKeyWordFilter: any): Promise<any> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/advancedFilter/save`, {
        advanceKeyWordFilter,
    });

    return data;
};

export const deleteAdvancedFilter = async (tenantId: number, filterId: string): Promise<any> => {
    const { data } = await axios.delete(`/api/tenants/${tenantId}/advancedFilter/${filterId}`);

    return data;
};
